import React from 'react';
import { Box, Flex, HStack, Icon, Input, InputGroup, InputLeftElement, Text, Textarea, VStack } from '@chakra-ui/react';
import { FiDollarSign, FiGift, FiPackage } from 'react-icons/fi';
import { Campaign, ContentRequirement, FoodfluenceLocation } from '@foodfluence/shared';
import LocationList from 'components/LocationList';
import { getContentTypeDisplayName, ugcDeliverableMatch } from '../../../utils/contentUtils';
import { isLocationfulCampaignType } from '../../../utils/collabUtils';
import UgcPieceIcon from './UgcPieceIcon';
import { getPresetSpec, getPresetTitle } from './UgcDeliverablesPicker';
import { ff } from '../../../theme';

const HOW_IT_WORKS: Record<string, string> = {
  shipToHome: 'Ships to you',
  shopify: 'Ships to you',
  inStore: 'Shoot at their location',
  retail: 'Pick up in a store',
  brandManaged: 'Logistics set with the brand',
};

type Row = { key: string; type: 'video' | 'photo'; title: string; spec: string; pieces: number; posted: boolean };

/** One row per kind of piece; a video requirement's count is how many videos, a photo set is one piece. */
const deliverableRows = (requirements: ContentRequirement[]): Row[] => {
  const rows = new Map<string, Row>();
  requirements.forEach((requirement) => {
    const preset = ugcDeliverableMatch({ type: requirement.type, duration: requirement.duration ?? null, count: requirement.count });
    const key = preset?.displayName ?? `${requirement.type}-${getContentTypeDisplayName(requirement.duration, requirement.type)}`;
    const row = rows.get(key) ?? {
      key,
      type: requirement.type === 'photo' ? 'photo' : 'video',
      title: preset ? getPresetTitle(preset) : getContentTypeDisplayName(requirement.duration, requirement.type),
      spec: preset ? getPresetSpec(preset) : requirement.type === 'photo' ? 'photo' : 'video',
      pieces: 0,
      posted: false,
    };
    row.pieces += requirement.type === 'video' ? requirement.count || 1 : 1;
    row.posted = row.posted || !!requirement.channels?.length;
    rows.set(key, row);
  });
  return Array.from(rows.values());
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text textStyle="eyebrow" color={ff.text2} mb="10px">
    {children}
  </Text>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <VStack
    spacing={0}
    align="stretch"
    bg="white"
    border="1px solid"
    borderColor={ff.line}
    borderRadius="20px"
    overflow="hidden"
    divider={<Box borderTopWidth="1px" borderColor={ff.line} />}
  >
    {children}
  </VStack>
);

const CompRow: React.FC<{ icon: React.ElementType; label: string; hint: string; amount: number }> = ({ icon, label, hint, amount }) => (
  <HStack px={4} py={3} spacing={3} minH="64px">
    <Flex bg={ff.grey} borderRadius="12px" w="36px" h="36px" align="center" justify="center" flexShrink={0}>
      <Icon as={icon} color={ff.ink} boxSize="16px" />
    </Flex>
    <Box flex={1}>
      <Text fontSize="15px" color={ff.ink} fontWeight="600">{label}</Text>
      <Text fontSize="13px" color={ff.text2}>{hint}</Text>
    </Box>
    <Text textStyle="display" fontSize="18px" color={ff.ink}>${amount}</Text>
  </HStack>
);

/** A comp row whose amount the creator can change; the brand's figure stays visible once it differs. */
const EditableCompRow: React.FC<{
  icon: React.ElementType;
  label: string;
  hint: string;
  amount: number;
  offered: number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ icon, label, hint, amount, offered, onChange }) => (
  <HStack px={4} py={3} spacing={3} minH="64px">
    <Flex bg={ff.grey} borderRadius="12px" w="36px" h="36px" align="center" justify="center" flexShrink={0}>
      <Icon as={icon} color={ff.ink} boxSize="16px" />
    </Flex>
    <Box flex={1}>
      <Text fontSize="15px" color={ff.ink} fontWeight="600">{label}</Text>
      <Text fontSize="13px" color={ff.text2}>{amount !== offered ? `Brand offers $${offered}` : hint}</Text>
    </Box>
    <InputGroup w="104px" flexShrink={0}>
      <InputLeftElement pointerEvents="none">
        <Text color={ff.text2} fontWeight={600}>$</Text>
      </InputLeftElement>
      <Input
        type="number"
        inputMode="numeric"
        min={0}
        value={amount}
        onChange={onChange}
        aria-label={`${label} you're asking for`}
        fontWeight={600}
        borderRadius="12px"
        borderColor={ff.border}
      />
    </InputGroup>
  </HStack>
);

interface UgcCampaignApplySummaryProps {
  campaign: Campaign;
  brandName: string;
  locations: FoodfluenceLocation[];
  isLoadingLocations?: boolean;
  selectedLocationIds: string[];
  onLocationClick: (docId: string) => void;
  /** The creator's ask: starts at the campaign's offer, and anything different goes to the brand as a proposal. */
  cashAmount: number;
  creditAmount: number;
  onCashChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCreditChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  note: string;
  onNoteChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  compensationRef?: React.Ref<HTMLDivElement>;
}

/** Applying to a UGC campaign: everything on one screen — what you'll make and what you'll get. */
const UgcCampaignApplySummary: React.FC<UgcCampaignApplySummaryProps> = ({
  campaign,
  brandName,
  locations,
  isLoadingLocations,
  selectedLocationIds,
  onLocationClick,
  cashAmount,
  creditAmount,
  onCashChange,
  onCreditChange,
  note,
  onNoteChange,
  compensationRef,
}) => {
  const rows = deliverableRows(Object.values(campaign.contentRequirements ?? {}));
  const ships = ['shipToHome', 'shopify', 'retail'].includes(campaign.campaignType);
  const productValue = Number(campaign.productValue) || 0;
  const hasCredit = campaign.campaignType === 'inStore' && campaign.credit > 0;

  return (
    <VStack spacing={5} align="stretch">
      <Box>
        <Text as="h2" textStyle="display" fontSize="24px" color={ff.ink}>{campaign.name}</Text>
        <Text fontSize="15px" color={ff.text2} mt={1} lineHeight="1.45">
          {HOW_IT_WORKS[campaign.campaignType] ?? 'Collab'} · You create the content, {brandName} posts it on their accounts.
        </Text>
      </Box>

      <Box>
        <SectionLabel>Your deliverables</SectionLabel>
        <Card>
          {rows.map((row) => (
            <HStack key={row.key} px={4} py={3} spacing={3} minH="64px">
              <UgcPieceIcon type={row.type} size={34} />
              <Box flex={1}>
                <Text fontSize="15px" color={ff.ink} fontWeight="600">{row.title}</Text>
                <Text fontSize="13px" color={ff.text2}>{row.spec}{row.posted ? ' · also posted to your socials' : ''}</Text>
              </Box>
              <Text textStyle="display" fontSize="18px" color={ff.ink}>{row.pieces}×</Text>
            </HStack>
          ))}
        </Card>
        <Text fontSize="13px" color={ff.text2} mt="8px" lineHeight="1.4">
          Deliverables are set by {brandName}. You can propose a different rate below.
        </Text>
      </Box>

      <Box ref={compensationRef} scrollMarginTop="16px">
        <SectionLabel>Compensation</SectionLabel>
        <Card>
          <EditableCompRow
            icon={FiDollarSign}
            label="Cash"
            hint="Paid after content is verified"
            amount={cashAmount}
            offered={campaign.cash || 0}
            onChange={onCashChange}
          />
          {hasCredit && (
            <EditableCompRow
              icon={FiGift}
              label="Credit"
              hint="To spend at their location"
              amount={creditAmount}
              offered={campaign.credit}
              onChange={onCreditChange}
            />
          )}
          {ships && productValue > 0 && (
            <CompRow
              icon={FiPackage}
              label="Gifted product"
              hint={campaign.campaignType === 'retail' ? 'Picked up at a store' : 'Shipped to you to shoot at home'}
              amount={productValue}
            />
          )}
        </Card>
      </Box>

      <Box>
        <SectionLabel>Note to {brandName} (optional)</SectionLabel>
        <Textarea
          value={note}
          onChange={onNoteChange}
          placeholder="Why this rate works, or anything they should know"
          minH="96px"
          borderRadius="16px"
          borderColor={ff.line}
        />
      </Box>

      {isLocationfulCampaignType(campaign.campaignType) && (
        <LocationList
          locations={locations}
          title="Choose a location"
          isLoading={isLoadingLocations}
          isSelectable
          selectedLocationIds={selectedLocationIds}
          onLocationClick={onLocationClick}
        />
      )}
    </VStack>
  );
};

export default UgcCampaignApplySummary;
